import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    AvatarGroupMoreTextsConstantType,
    AvatarGroupSlotStyleConstantType,
    AvatarGroupAddStyleConstantType,
    AvatarGroupLabelConstantType,
    AvatarGroupSlotsConstantType,
    AvatarGroupSizeConstantType,
    AvatarGroupDynamicStyleType,
    AvatarGroupMaxConstantType,
    AvatarGroupMoreTextsType,
    AvatarGroupMeasures,
    AvatarGroupSlotType
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    AVATAR_STATUS_LABELS,
    getAvatarShadow,
    getAvatarSize
} from "../avatar/stylesheet";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    formatNumber
} from "../../helpers/locale/format";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    webStyle
} from "../../utils";

export const AVATAR_GROUP_NAME_SEPARATOR = ", ";

export const AVATAR_GROUP_MAX_MEMBERS = 4;

export const AVATAR_GROUP_TOOLTIP_NAMES = 3;

export const AVATAR_GROUP_TOOLTIP_LINE_SEPARATOR = "\n";

export const getAvatarGroupMax = ({
    max = AVATAR_GROUP_MAX_MEMBERS
}: AvatarGroupMaxConstantType): number => {
    if(Number.isNaN(max)) {
        return AVATAR_GROUP_MAX_MEMBERS;
    }

    return Math.max(1, Math.floor(max));
};

export const getAvatarGroupSize = ({
    avatarSizes,
    iconSizes,
    borders,
    size
}: AvatarGroupSizeConstantType): AvatarGroupMeasures => {
    const currentSize = getAvatarSize({
        avatarSizes,
        iconSizes,
        borders,
        size
    });

    return {
        ...currentSize,
        overlap: currentSize.size / 4 - borders.subtract
    };
};

export const getAvatarGroupSlots = ({
    isLoading,
    avatars,
    hasAdd,
    max
}: AvatarGroupSlotsConstantType): Array<AvatarGroupSlotType> => {
    const overflowCount = avatars.length - max;

    const slots: Array<AvatarGroupSlotType> = avatars.slice(0, max).map((avatar) => ({
        kind: "member",
        avatar
    }));

    if(overflowCount > 0) {
        slots.push({
            hiddenAvatars: avatars.slice(max),
            count: overflowCount,
            kind: "more"
        });
    }

    const currentSlots: Array<AvatarGroupSlotType> = isLoading ? [
        ...slots.slice(0, -1),
        {
            kind: "loading"
        }
    ] : slots;

    if(!hasAdd) {
        return currentSlots;
    }

    return [
        ...currentSlots,
        {
            kind: "add"
        }
    ];
};

export const getAvatarGroupMoreTexts = ({
    hiddenAvatars,
    localize,
    locale
}: AvatarGroupMoreTextsConstantType): AvatarGroupMoreTextsType => {
    const names = hiddenAvatars.reduce<Array<string>>((list, avatarItem) => {
        const name = avatarItem.title?.trim();

        return name ? [
            ...list,
            name
        ] : list;
    }, []).slice(0, AVATAR_GROUP_TOOLTIP_NAMES);

    const restCount = formatNumber({
        value: hiddenAvatars.length - names.length,
        locale
    });

    const totalCount = formatNumber({
        value: hiddenAvatars.length,
        locale
    });

    const isRest = hiddenAvatars.length > names.length;

    if(!names.length) {
        return {
            label: localize("avatar-group-more-people", [
                `+${totalCount}`
            ]),
            title: localize("avatar-group-more-people", [
                totalCount
            ])
        };
    }

    const title = isRest ? [
        ...names,
        localize("avatar-group-and-more-people", [
            restCount
        ])
    ].join(AVATAR_GROUP_TOOLTIP_LINE_SEPARATOR) : names.join(AVATAR_GROUP_TOOLTIP_LINE_SEPARATOR);

    const namesLabel = isRest ? `${names.join(AVATAR_GROUP_NAME_SEPARATOR)} ${localize("avatar-group-and-more-people", [
        restCount
    ])}` : names.join(AVATAR_GROUP_NAME_SEPARATOR);

    return {
        label: localize("avatar-group-more-names", [
            totalCount,
            namesLabel
        ]),
        title
    };
};

export const getAvatarGroupMembersLabel = ({
    isStatusIndicator,
    localize,
    avatars,
    locale,
    max
}: AvatarGroupLabelConstantType): string | undefined => {
    const names = avatars.slice(0, max).reduce<Array<string>>((list, avatarItem) => {
        const name = avatarItem.title?.trim();

        if(!name) {
            return list;
        }

        const badgeLabel = avatarItem.badge?.accessibilityLabel?.trim();

        if(badgeLabel) {
            return [
                ...list,
                localize("avatar-group-member-status", [
                    name,
                    badgeLabel
                ])
            ];
        }

        if(!isStatusIndicator) {
            return [
                ...list,
                name
            ];
        }

        return [
            ...list,
            localize("avatar-group-member-status", [
                name,
                localize(AVATAR_STATUS_LABELS[avatarItem.statusIndicatorType ?? "online"])
            ])
        ];
    }, []);

    if(!names.length) {
        return undefined;
    }

    const restCount = avatars.length - max;

    if(restCount > 0) {
        return localize("avatar-group-more", [
            names.join(AVATAR_GROUP_NAME_SEPARATOR),
            formatNumber({
                value: restCount,
                locale
            })
        ]);
    }

    if(names.length === 1) {
        return names.join(AVATAR_GROUP_NAME_SEPARATOR);
    }

    return localize("avatar-group-and", [
        names.slice(0, -1).join(AVATAR_GROUP_NAME_SEPARATOR),
        names.slice(-1).join(AVATAR_GROUP_NAME_SEPARATOR)
    ]);
};

export const getAvatarGroupSlotStyle = ({
    shapeConfigs,
    currentSize,
    slotCount,
    index
}: AvatarGroupSlotStyleConstantType): ViewStyle => {
    const slotStyle: Mutable<ViewStyle> = {
        zIndex: shapeConfigs.layers.raised + slotCount - index
    };

    if(index > 0) {
        slotStyle.marginStart = -currentSize.overlap;
    }

    return slotStyle;
};

export const getAvatarGroupAddStyle = ({
    isFocusVisible,
    currentSize,
    isDisabled,
    fillColor,
    ringColor,
    radiuses,
    borders,
    colors
}: AvatarGroupAddStyleConstantType): ViewStyle => {
    return {
        borderColor: isDisabled ? colors.system.state.border.disabled.neutral : colors.content.icon.low,
        boxShadow: getAvatarShadow({
            ringColor,
            borders
        }),
        backgroundColor: fillColor,
        ...getFocusRingStyle({
            colorKey: "default",
            isFocusVisible,
            borders,
            colors
        }),
        borderRadius: radiuses.full,
        borderWidth: borders.line,
        height: currentSize.size,
        width: currentSize.size
    };
};

const stylesheet = StyleSheet.create({
    add: {
        justifyContent: "center",
        borderStyle: "dashed",
        alignItems: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    container: {
        justifyContent: "center",
        alignItems: "flex-start",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    more: {
        justifyContent: "center",
        alignItems: "center"
    },
    row: {
        flexDirection: "row",
        alignItems: "center"
    },
    surface: {
        flexDirection: "row",
        position: "relative",
        alignItems: "center"
    },
    content: {
        flexDirection: "row",
        alignItems: "center"
    },
    moreTitle: {
        textAlign: "center"
    }
});

export const useStyles = ({
    isFocusVisible,
    shapeConfigs,
    touchTargets,
    currentSize,
    pointerType,
    moreColors,
    isLoading,
    ringColor,
    slotCount,
    radiuses,
    borders,
    colors,
    spaces
}: AvatarGroupDynamicStyleType) => {
    const hitArea = getHitArea({
        visualSize: currentSize.size + spaces.spacingXxs * 2,
        touchTargets,
        pointerType
    });

    const styles = {
        focus: {
            zIndex: shapeConfigs.layers.raised + slotCount + 1
        } as Mutable<ViewStyle>,
        members: {
            zIndex: shapeConfigs.layers.raised + slotCount
        } as Mutable<ViewStyle>,
        more: {
            backgroundColor: moreColors.containerColor,
            boxShadow: getAvatarShadow({
                ringColor,
                borders
            }),
            borderRadius: radiuses.full,
            height: currentSize.size,
            width: currentSize.size
        } as Mutable<ViewStyle>,
        slots: Array.from({
            length: slotCount
        }, (_, index) => getAvatarGroupSlotStyle({
            shapeConfigs,
            currentSize,
            slotCount,
            index
        })),
        container: {
            minHeight: hitArea.minHeight,
            minWidth: hitArea.minWidth
        } as Mutable<ViewStyle>,
        surface: {
            borderRadius: radiuses.full,
            padding: spaces.spacingXxs
        } as Mutable<ViewStyle>
    };

    Object.assign(styles.surface, getFocusRingStyle({
        colorKey: "default",
        isFocusVisible,
        borders,
        colors
    }));

    if(isLoading) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    return styles;
};
export default stylesheet;
